/**
 * 单据浏览器打印（docs/05 §6-7 / §10）：
 * 不引入打印模板引擎，先用 `cfg_print_tpl` 的「标准单据」版式兜底。
 * 通过隐藏 iframe 构建独立文档后打印，避免把侧栏/工具栏一起打进小票。
 */
export interface PrintColumn {
  title: string;
  align?: 'left' | 'center' | 'right';
}

export interface PrintDocumentOptions {
  title: string;
  /** 右上角单号等。 */
  subtitle?: string;
  /** 抬头信息（单号 / 科室 / 日期 …）。 */
  meta?: [label: string, value: string][];
  columns: PrintColumn[];
  rows: (string | number)[][];
  footer?: string;
}

const escapeHtml = (value: unknown): string =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

function buildHtml(options: PrintDocumentOptions): string {
  const meta = (options.meta ?? [])
    .map(([label, value]) => `<div class="meta-item"><span>${escapeHtml(label)}</span><b>${escapeHtml(value)}</b></div>`)
    .join('');

  const head = options.columns
    .map((col) => `<th style="text-align:${col.align ?? 'left'}">${escapeHtml(col.title)}</th>`)
    .join('');

  const body = options.rows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell, index) =>
              `<td style="text-align:${options.columns[index]?.align ?? 'left'}">${escapeHtml(cell)}</td>`
          )
          .join('')}</tr>`
    )
    .join('');

  return `<!doctype html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(options.title)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 24px; font: 12px/1.6 -apple-system, "Microsoft YaHei", sans-serif; color: #111; }
  h1 { font-size: 20px; margin: 0; letter-spacing: 2px; }
  .subtitle { color: #555; font-size: 12px; }
  header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid #111; padding-bottom: 8px; }
  .meta { display: flex; flex-wrap: wrap; gap: 4px 24px; margin: 12px 0; }
  .meta-item span { color: #666; margin-right: 6px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th, td { border: 1px solid #333; padding: 5px 6px; }
  th { background: #f0f0f0; font-weight: 600; }
  tfoot td { border: none; padding-top: 16px; color: #666; }
  @page { margin: 12mm; }
</style>
</head>
<body>
  <header>
    <h1>${escapeHtml(options.title)}</h1>
    <div class="subtitle">${escapeHtml(options.subtitle ?? '')}</div>
  </header>
  <div class="meta">${meta}</div>
  <table>
    <thead><tr>${head}</tr></thead>
    <tbody>${body || `<tr><td colspan="${options.columns.length}" style="text-align:center;color:#888">无明细</td></tr>`}</tbody>
    <tfoot><tr><td colspan="${options.columns.length}">${escapeHtml(options.footer ?? '')}</td></tr></tfoot>
  </table>
</body>
</html>`;
}

/** 打开打印预览；用户取消不会残留 iframe。 */
export function printDocument(options: PrintDocumentOptions): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  document.body.appendChild(frame);

  const cleanup = () => {
    window.setTimeout(() => frame.remove(), 0);
  };

  const doc = frame.contentDocument;
  if (!doc) {
    cleanup();
    return;
  }
  doc.open();
  doc.write(buildHtml(options));
  doc.close();

  frame.onload = () => {
    try {
      frame.contentWindow?.focus();
      frame.contentWindow?.print();
    } finally {
      cleanup();
    }
  };

  // 部分浏览器（Safari）不触发 onload，兜底直接打印
  if (doc.readyState === 'complete') frame.onload?.(new Event('load'));
}
